import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Plus, Trash, FloppyDisk, Info, CircleNotch, ArrowUUpLeft,
} from "@phosphor-icons/react";

/**
 * v0.97-d — Aba "Intervalos" na Central de Operação.
 *
 * Expõe pra UI o backend já existente (`GET/PUT /api/tenants/me/intervals`).
 * Permite o owner customizar prazos de follow-up/campanha (ex: contactar
 * cliente 30d após revisão), sobrescrevendo os defaults do setor.
 *
 * UX: lista o padrão do setor (read-only, chip cinza) + a tabela editável
 * dos custom. Diff visível → owner sabe o que está herdando vs mudou.
 */
export default function IntervalsTab({ onChange, onToast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sectorDefaults, setSectorDefaults] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [sectorCode, setSectorCode] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tenant/intervals");
      setSectorDefaults(r.data?.sector_defaults || []);
      setCustoms(r.data?.custom || []);
      setSectorCode(r.data?.sector_code || "");
    } catch (e) {
      onToast?.("Falha ao carregar intervalos", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addRow = () =>
    setCustoms([...customs, { key: "", interval_days: 30, note: "" }]);

  const rmRow = (i) => setCustoms(customs.filter((_, idx) => idx !== i));

  const patchRow = (i, k, v) =>
    setCustoms(customs.map((row, idx) =>
      idx === i ? { ...row, [k]: v } : row
    ));

  const save = async () => {
    // Validação client-side
    const cleaned = customs
      .map((r) => ({
        key: (r.key || "").trim(),
        interval_days: parseInt(r.interval_days, 10) || 0,
        note: (r.note || "").trim(),
      }))
      .filter((r) => r.key && r.interval_days > 0);

    // keys únicas
    const keys = cleaned.map((r) => r.key.toLowerCase());
    if (new Set(keys).size !== keys.length) {
      onToast?.("Cada regra precisa de uma chave única.", "error");
      return;
    }

    setSaving(true);
    try {
      await api.put("/tenant/intervals", { intervals: cleaned });
      onToast?.("Intervalos salvos.");
      onChange?.();
      load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Falha ao salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (customs.length === 0) return;
    if (!window.confirm(
      "Voltar aos padrões do setor? Todos os intervalos customizados serão perdidos."
    )) return;
    setSaving(true);
    try {
      await api.put("/tenant/intervals", { intervals: [] });
      onToast?.("Voltamos aos padrões do setor.");
      onChange?.();
      load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Falha ao resetar.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center text-slate-400">
        <CircleNotch size={20} className="animate-spin mr-2" />
        Carregando…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="intervals-tab">
      {/* Defaults do setor */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Padrões do setor
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              O que o AgentUS usa se você não customizar nada.
              {sectorCode && (
                <span className="ml-1 text-[11px] px-1.5 py-0.5 bg-slate-100 rounded font-mono">
                  {sectorCode}
                </span>
              )}
            </p>
          </div>
        </div>

        {sectorDefaults.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 border border-dashed border-slate-200 rounded-lg text-center">
            Este setor não tem intervalos padrão.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
            {sectorDefaults.map((d, i) => (
              <li
                key={i}
                className="px-4 py-2.5 flex items-center justify-between bg-white"
                data-testid={`intervals-default-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800">{d.label || "—"}</div>
                  {d.match?.service_prefix && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Aplica quando o serviço começa com:{" "}
                      <span className="font-mono bg-slate-50 px-1.5 py-0.5 rounded">
                        {d.match.service_prefix}
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-xs text-slate-500 tabular-nums whitespace-nowrap ml-3">
                  {d.interval_days} dia(s)
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Custom intervals */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Seus intervalos customizados
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Sobrescreve o padrão. Use uma chave curta (ex: <code className="text-[11px] bg-slate-100 px-1 rounded">followup_2m</code>) e o número de dias.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={resetToDefaults}
              disabled={saving || customs.length === 0}
              data-testid="intervals-reset"
              className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowUUpLeft size={12} /> Voltar aos padrões
            </button>
          </div>
        </div>

        {customs.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center text-sm text-slate-400">
            <Info size={16} className="mx-auto mb-2 text-slate-300" />
            Nenhum intervalo customizado. O AgentUS usa os padrões do setor.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-600">
                  <th className="px-3 py-2 font-medium text-xs uppercase tracking-wide">
                    Chave
                  </th>
                  <th className="px-3 py-2 font-medium text-xs uppercase tracking-wide w-32">
                    Dias
                  </th>
                  <th className="px-3 py-2 font-medium text-xs uppercase tracking-wide">
                    Nota
                  </th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {customs.map((row, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <input
                        value={row.key}
                        onChange={(e) => patchRow(i, "key", e.target.value)}
                        placeholder="ex: followup_2m"
                        data-testid={`intervals-key-${i}`}
                        className="w-full border border-slate-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:border-slate-900"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="1"
                        max="3650"
                        value={row.interval_days}
                        onChange={(e) => patchRow(i, "interval_days", e.target.value)}
                        data-testid={`intervals-days-${i}`}
                        className="w-full border border-slate-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:border-slate-900 tabular-nums"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        value={row.note || ""}
                        onChange={(e) => patchRow(i, "note", e.target.value)}
                        placeholder="Opcional"
                        data-testid={`intervals-note-${i}`}
                        className="w-full border border-slate-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:border-slate-900"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => rmRow(i)}
                        data-testid={`intervals-remove-${i}`}
                        className="text-slate-400 hover:text-rose-600"
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={addRow}
            data-testid="intervals-add"
            className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <Plus size={12} /> Adicionar regra
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="intervals-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 disabled:opacity-50"
          >
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </section>
    </div>
  );
}
